"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from "react";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import Button from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DialogBody, DialogFooter } from "@/components/ui/dialog";
import HeightInput from "@/components/ui/height-input";
import { Label } from "@/components/ui/label";
import Switch from "@/components/ui/switch";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useDebounce } from "@/hooks/use-debounce";
import type { ClinicClock } from "@/lib/clinic-clock";
import { getOptionLabel } from "@/lib/measurement-calculations";
import { type ResolvedItem, computeSaveTimestamp, feetInchesToTotalInches, formatHeightDisplay, inchesToFeetInches, isHeightField, isNumericDataType } from "@/lib/measurement-utils";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import type { MeasurementField, MeasurementGroupWithFields, MeasurementValueCreate, MeasurementValueSummary, PrefillValue } from "@/types/measurement";
import { Delta } from "./delta-display";
import { MeasurementInput } from "./measurement-input";
import { MeasurementSourceBadge } from "./measurement-source-badge";
import { useRecordMeasurementsMutation } from "@/query/patient/measurements";
type SectionHeader = Extract<ResolvedItem, {
  type: "section-header";
}>;
type FieldItem = Extract<ResolvedItem, {
  type: "field";
}>;
type RecordSection = {
  header: SectionHeader | null;
  items: FieldItem[];
};
type FieldValue = string | string[];
type FormValues = Record<string, FieldValue>;
interface RecordFormProps {
  group: MeasurementGroupWithFields;
  patientId: string;
  noteId?: string;
  eventId?: string;
  measurementDate: Date | undefined;
  /** The clock `measurementDate` is a day on; the clinic's by default. */
  clock?: ClinicClock;
  sections: RecordSection[];
  fieldItems: FieldItem[];
  prefillData: Map<string, PrefillValue>;
  lastPriorByField: Map<string, MeasurementValueSummary>;
  onClose: () => void;
}
function mergeFieldItem(existing: FieldItem, item: FieldItem): FieldItem {
  if (existing.membership.isRequired || !item.membership.isRequired) {
    return existing;
  }
  return {
    ...existing,
    membership: {
      ...existing.membership,
      isRequired: true
    }
  };
}
function dedupeFieldItems(items: FieldItem[]): FieldItem[] {
  const byFieldId = new Map<string, FieldItem>();
  const fieldIds: string[] = [];
  items.forEach(item => {
    const fieldId = item.field.id;
    const existing = byFieldId.get(fieldId);
    if (existing) {
      byFieldId.set(fieldId, mergeFieldItem(existing, item));
      return;
    }
    byFieldId.set(fieldId, item);
    fieldIds.push(fieldId);
  });
  return fieldIds.map(fieldId => byFieldId.get(fieldId)!);
}
function dedupeSections(sections: RecordSection[], fieldItems: FieldItem[]): RecordSection[] {
  const byFieldId = new Map(fieldItems.map(item => [item.field.id, item]));
  const seen = new Set<string>();
  const dedupedSections: RecordSection[] = [];
  sections.forEach(section => {
    const items: FieldItem[] = [];
    section.items.forEach(item => {
      const fieldId = item.field.id;
      if (seen.has(fieldId)) return;
      seen.add(fieldId);
      items.push(byFieldId.get(fieldId) ?? item);
    });
    if (items.length > 0) {
      dedupedSections.push({
        ...section,
        items
      });
    }
  });
  return dedupedSections;
}
function formatPrefillValue(pv: PrefillValue, field: MeasurementField) {
  if (isHeightField(field) && pv.numericValue != null) {
    return formatHeightDisplay(pv.numericValue);
  }
  if (pv.arrayValue && pv.arrayValue.length > 0) {
    // For option-backed STRING_ARRAY fields, surface the label not the raw
    // stored code; falls back to the raw value when no option matches.
    return pv.arrayValue.map(v => getOptionLabel(field, v)).join(", ");
  }
  if (pv.numericValue != null && field.options?.length) {
    return getOptionLabel(field, pv.numericValue);
  }
  if (pv.textValue != null && field.options?.length) {
    return getOptionLabel(field, pv.textValue);
  }
  const val = pv.numericValue != null ? String(pv.numericValue) : pv.textValue ?? "";
  return field.unit ? `${val} ${field.unit}` : val;
}
function FieldDelta(t0) {
  const $ = _c(14);
  const {
    field,
    fieldKey,
    lastPrior,
    isSkipped,
    showDeltas,
    showTrends,
    control
  } = t0;
  let t1;
  if ($[0] !== control || $[1] !== fieldKey) {
    t1 = {
      control,
      name: fieldKey
    };
    $[0] = control;
    $[1] = fieldKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const watched = useWatch(t1);
  const debounced = useDebounce(watched, 300);
  if (!showDeltas || isSkipped || !lastPrior || lastPrior.numericValue == null) {
    return null;
  }
  if (Array.isArray(debounced)) {
    return null;
  }
  const current = parseFloat(debounced ?? "");
  if (isNaN(current)) {
    return null;
  }
  const rawChange = current - lastPrior.numericValue;
  const change = rawChange === 0 ? null : rawChange;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Delta.Empty>—</Delta.Empty>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== showTrends) {
    t3 = showTrends && <Delta.Icon />;
    $[4] = showTrends;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Delta.Value />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== lastPrior.measurementDate) {
    t5 = lastPrior.measurementDate && <Delta.Since date={new Date(lastPrior.measurementDate)} />;
    $[7] = lastPrior.measurementDate;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== change || $[10] !== field || $[11] !== t3 || $[12] !== t5) {
    t6 = <Delta change={change} field={field} size="xs" className="inline-flex items-center gap-1">{t2}{t3}{t4}{t5}</Delta>;
    $[9] = change;
    $[10] = field;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
export default function RecordForm({
  group,
  patientId,
  noteId,
  eventId,
  measurementDate,
  clock,
  sections,
  fieldItems,
  prefillData,
  lastPriorByField,
  onClose
}: RecordFormProps) {
  const viewerClock = useClinicClock();
  const [skippedFields, setSkippedFields] = useState<Set<string>>(() => new Set(prefillData.keys()));
  const recordMutation = useRecordMeasurementsMutation();
  const recordFieldItems = useMemo(() => dedupeFieldItems(fieldItems), [fieldItems]);
  const recordSections = useMemo(() => dedupeSections(sections, recordFieldItems), [sections, recordFieldItems]);
  const formValues = useMemo(() => {
    const vals: FormValues = {};
    prefillData.forEach((pv, fieldId) => {
      if (pv.arrayValue && pv.arrayValue.length > 0) {
        vals[fieldId] = pv.arrayValue;
      } else if (pv.numericValue != null) {
        vals[fieldId] = String(pv.numericValue);
      } else {
        vals[fieldId] = pv.textValue ?? "";
      }
    });
    return vals;
  }, [prefillData]);
  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: {
      errors
    }
  } = useForm<FormValues>({
    mode: "onSubmit",
    values: formValues,
    resetOptions: {
      keepDirtyValues: true
    }
  });
  const toggleSkip = (fieldId_0: string) => {
    setSkippedFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldId_0)) {
        next.delete(fieldId_0);
      } else {
        next.add(fieldId_0);
      }
      return next;
    });
  };
  const prefillFieldIds = useMemo(() => new Set(prefillData.keys()), [prefillData]);
  const allPrefillSkipped = prefillFieldIds.size > 0 && Array.from(prefillFieldIds).every(id => skippedFields.has(id));
  const toggleAllPrefill = () => {
    setSkippedFields(prev_0 => {
      const next_0 = new Set(prev_0);
      const ids = Array.from(prefillFieldIds);
      if (allPrefillSkipped) {
        ids.forEach(id_0 => next_0.delete(id_0));
      } else {
        ids.forEach(id_1 => next_0.add(id_1));
      }
      return next_0;
    });
  };
  const handleFormSubmit = async (data: FormValues) => {
    clearErrors("root");
    const values: MeasurementValueCreate[] = [];
    for (const item of recordFieldItems) {
      if (skippedFields.has(item.field.id)) continue;
      const raw = data[item.field.id];
      if (item.field.dataType === "STRING_ARRAY") {
        if (!Array.isArray(raw) || raw.length === 0) continue;
        values.push({
          fieldId: item.field.id,
          arrayValue: raw
        });
        continue;
      }
      if (typeof raw !== "string" || !raw) continue;
      if (isNumericDataType(item.field.dataType)) {
        values.push({
          fieldId: item.field.id,
          numericValue: parseFloat(raw)
        });
      } else {
        values.push({
          fieldId: item.field.id,
          textValue: raw
        });
      }
    }
    if (values.length === 0) {
      setError("root", {
        type: "manual",
        message: "Please enter at least one measurement value."
      });
      return;
    }
    try {
      const existingDates: string[] = [];
      prefillData.forEach(pv_0 => {
        if (pv_0.measurementDate) existingDates.push(pv_0.measurementDate);
      });
      const dayClock = clock ?? viewerClock;
      const stamp = computeSaveTimestamp(measurementDate ?? dayClock.todayMarker(), existingDates, new Date(), dayClock.timeZone);
      await recordMutation.mutateAsync({
        patientId,
        measurementDate: stamp.toISOString(),
        values,
        noteId: noteId ?? null,
        eventId: eventId ?? null
      });
      toast({
        title: "Recorded",
        description: `${group.name} measurements saved.`
      });
      onClose();
    } catch {
      // Error toast handled by mutation hook
    }
  };
  return <form onSubmit={handleSubmit(handleFormSubmit)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <DialogBody>
        <div className="space-y-4">
          {prefillFieldIds.size > 0 && <div className="rounded-md bg-status-info-bg px-3 py-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="flex items-center gap-1 text-caption text-status-info-text">
                    <FiCheck size={12} />
                    <span>
                      {prefillFieldIds.size} field
                      {prefillFieldIds.size !== 1 ? <span>{"s"}</span> : <span>{""}</span>}
                      <span> recorded today</span>
                    </span>
                  </span>
                  <p className="mt-0.5 text-caption text-foreground-secondary">
                    Enabled fields will have new entries recorded
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-caption text-foreground-secondary">
                    {allPrefillSkipped ? "Keep existing" : "Record new"}
                  </span>
                  <Switch size="small" checked={!allPrefillSkipped} onCheckedChange={toggleAllPrefill} data-testid="skip-all-toggle" />
                </div>
              </div>
            </div>}

          {recordSections.map((section, sectionIdx) => {
          const renderFields = () => section.items.map(resolvedItem => {
            const {
              field,
              membership
            } = resolvedItem;
            const fieldKey = field.id;
            const prefillValue = prefillData.get(field.id);
            const isSkipped = skippedFields.has(field.id);
            const isHeight = isHeightField(field);
            return <div key={field.id} data-testid={`record-field-${field.id}`} className={cx("space-y-1 rounded-md transition-opacity", isSkipped && "opacity-50")}>
                    <div className="flex items-center justify-between">
                      <Label>
                        {field.name}
                        {field.unit && !isHeight && <span className="ml-1 text-caption text-muted-foreground">
                            ({field.unit})
                          </span>}
                        {membership.isRequired && <span className="ml-0.5 text-status-error-text">
                            *
                          </span>}
                      </Label>
                      {prefillValue && <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-caption text-status-info-text">
                            <FiCheck size={10} />
                            Last: {formatPrefillValue(prefillValue, field)}
                          </span>
                          <MeasurementSourceBadge isUserReported={prefillValue.isUserReported} compact />
                          <Switch size="small" data-testid={`field-skip-toggle-${field.id}`} checked={!isSkipped} onCheckedChange={() => toggleSkip(field.id)} />
                        </div>}
                    </div>
                    {isHeight ? <Controller name={fieldKey} control={control} rules={{
                required: membership.isRequired && !isSkipped ? `${field.name} is required` : false,
                validate: value => {
                  if (isSkipped) return true;
                  const raw_0 = typeof value === "string" ? value : "";
                  if (!raw_0 && !membership.isRequired) return true;
                  // Height is stored as total inches string in form
                  const num = parseFloat(raw_0);
                  if (raw_0 && isNaN(num)) return "Must be a valid height";
                  return true;
                }
              }} render={({
                field: controllerField
              }) => {
                const rawValue = typeof controllerField.value === "string" ? controllerField.value : "";
                const totalInches = parseFloat(rawValue || "0");
                const {
                  feet,
                  inches
                } = isNaN(totalInches) ? {
                  feet: 0,
                  inches: 0
                } : inchesToFeetInches(totalInches);
                return <HeightInput data-testid={`field-input-${field.id}`} value={{
                  feet: totalInches ? String(feet) : "",
                  inches: totalInches ? String(inches) : ""
                }} onChange={hv => {
                  const ft = parseInt(hv.feet, 10) || 0;
                  const inc = parseFloat(hv.inches) || 0;
                  if (!hv.feet && !hv.inches) {
                    controllerField.onChange("");
                  } else {
                    controllerField.onChange(String(feetInchesToTotalInches(ft, inc)));
                  }
                }} disabled={isSkipped} quarterInches hasError={!!errors[fieldKey]} errorMessage={errors[fieldKey]?.message as string} />;
              }} /> : <MeasurementInput field={field} control={control} name={fieldKey} disabled={isSkipped} hasError={!!errors[fieldKey]} errorMessage={errors[fieldKey]?.message as string} placeholder={field.unit ? `Enter ${field.name.toLowerCase()}` : "Enter value"} rules={{
                validate: value_0 => {
                  if (isSkipped) return true;
                  if (field.dataType === "STRING_ARRAY") {
                    const arr = Array.isArray(value_0) ? value_0 : [];
                    if (membership.isRequired && arr.length === 0) {
                      return `${field.name} is required`;
                    }
                    return true;
                  }
                  const raw_1 = typeof value_0 === "string" ? value_0 : "";
                  if (membership.isRequired && !raw_1) {
                    return `${field.name} is required`;
                  }
                  if (!raw_1) return true;
                  if (!isNumericDataType(field.dataType)) {
                    return true;
                  }
                  const num_0 = parseFloat(raw_1);
                  if (isNaN(num_0)) return "Must be a number";
                  if (field.minValue != null && num_0 < field.minValue) {
                    return `Minimum value is ${field.minValue}`;
                  }
                  if (field.maxValue != null && num_0 > field.maxValue) {
                    return `Maximum value is ${field.maxValue}`;
                  }
                  return true;
                }
              }} />}
                    <FieldDelta field={field} fieldKey={fieldKey} lastPrior={lastPriorByField.get(field.id)} isSkipped={isSkipped} showDeltas={group.displayConfig?.showDeltas !== false} showTrends={group.displayConfig?.showTrends !== false} control={control} />
                  </div>;
          });
          if (section.header) {
            return <Collapsible key={`section-${sectionIdx}`} defaultOpen>
                  <CollapsibleTrigger data-testid={`section-header-${section.header.groupName}`} className="flex w-full items-center gap-2 border-b border-border pb-1 pt-2">
                    {section.header.groupColor && <span className="inline-block h-2 w-2 rounded-full" style={{
                  backgroundColor: section.header.groupColor
                }} />}
                    <span className="text-caption-emphasis text-foreground-secondary">
                      {section.header.groupName}
                    </span>
                    <FiChevronDown className="ml-auto h-3 w-3 text-foreground-secondary transition-transform [[data-state=open]>&]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-4 pt-2">{renderFields()}</div>
                  </CollapsibleContent>
                </Collapsible>;
          }
          return <React.Fragment key={`section-${sectionIdx}`}>
                {renderFields()}
              </React.Fragment>;
        })}
        </div>
      </DialogBody>

      {errors.root && <p className="px-4 pb-2 text-body text-status-error-text">
          {errors.root.message}
        </p>}

      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" data-testid="record-save-button" isLoading={recordMutation.isPending}>
          Save
        </Button>
      </DialogFooter>
    </form>;
}